//DraggableColumnHeader.tsx
import {
  Header,
  Table,
  ColumnOrderState,
  Updater,
  flexRender,
} from '@tanstack/react-table';

import { Draggable, Droppable } from 'react-beautiful-dnd';
import styled from 'styled-components';

import HorizontalResizer from '@paradigm/design-system/src/assets/HorizontalResizer';
import { paradigmLightBlue } from '@paradigm/design-system/src/colors';
import SorterComponent from './Sorter';

interface DraggableColumnHeaderProps<T, V> {
  header: Header<T, V>;
  sortSide?: 'left' | 'right';
  table: Table<T>;
  columnOrder?: ColumnOrderState;
  setColumnOrder?: (updater: Updater<ColumnOrderState>) => void;
}

const DraggableColumnHeader = <T, V>({
  header,
  table: _table,
  sortSide = 'left',
  columnOrder = [],
}: DraggableColumnHeaderProps<T, V>) => {
  const { column } = header;

  const isDraggable = column.columnDef.meta?.draggable ?? true;
  const isSelectColumn = column.columnDef.meta?.isSelectColumn ?? false;
  const isColumnSorted = column.getIsSorted() !== false;
  const toggleSortDirection = column.getToggleSortingHandler();

  return (
    <Droppable droppableId={column.id}>
      {(droppableProvided, droppableSnapshot) => (
        <HeaderComponent
          {...droppableProvided.droppableProps}
          ref={droppableProvided.innerRef}
          isDragging={droppableSnapshot.isDraggingOver}
        >
          <HeaderContainer>
            {header.isPlaceholder ? (
              <Placeholder />
            ) : (
              <HeaderBody
                isColumnSorted={isColumnSorted}
                onClick={toggleSortDirection}
                data-testid={`${column.id}-header`}
              >
                <Resizer
                  onMouseDown={header.getResizeHandler()}
                  hidden={!header.column.getCanResize()}
                >
                  <HorizontalResizer />
                </Resizer>

                {sortSide === 'left' && <SorterComponent header={header} />}
                {isDraggable && !isSelectColumn ? (
                  <Draggable
                    draggableId={column.id}
                    index={columnOrder.indexOf(column.id)}
                  >
                    {(draggableProvider, draggableSnapshot) => (
                      <Dragger
                        isDragging={draggableSnapshot.isDragging}
                        ref={draggableProvider.innerRef}
                        {...draggableProvider.draggableProps}
                        {...draggableProvider.dragHandleProps}
                        style={draggableProvider.draggableProps.style}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </Dragger>
                    )}
                  </Draggable>
                ) : (
                  flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )
                )}
                {sortSide === 'right' && <SorterComponent header={header} />}
              </HeaderBody>
            )}
          </HeaderContainer>
          {droppableProvided.placeholder}
        </HeaderComponent>
      )}
    </Droppable>
  );
};

const Resizer = styled.div`
  display: flex;
  font-weight: 400;
  cursor: col-resize;
  user-select: none;
  touch-action: none;
  color: ${paradigmLightBlue};
  opacity: 0.7;
  padding-right: 4px;
`;

const HeaderContainer = styled.div`
  width: 100%;
  height: 100%;
`;

const HeaderBody = styled.div<{ isColumnSorted: boolean }>`
  display: flex;
  height: 100%;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  user-select: none;
  font-weight: 400;
  color: ${({ isColumnSorted }) =>
    isColumnSorted ? paradigmLightBlue : 'inherit'};
`;

const Dragger = styled.div<{ isDragging: boolean }>`
  display: flex;
  flex: 1;

  cursor: ${({ isDragging }) => (isDragging ? 'grabbing' : ' grab')};
`;

const Placeholder = styled.div``;

const HeaderComponent = styled.th<{ isDragging?: boolean }>`
  opacity: ${({ isDragging }) => (isDragging === true ? 0.7 : 1)};
`;

export default DraggableColumnHeader;
